"use client";

import type { UpdateDefaultTemplateRequest } from "@/features/calendar/types/schedule-settings";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { SettingsCard } from "./SettingsCard.tsx";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTemplatesStore } from "@/features/calendar/store/useTemplatesStore";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";

// Define the form schema with zod
const templateSettingsSchema = z.object({
  defaultTemplateId: z.string().nullable(),
});

type TemplateSettingsFormValues = z.infer<typeof templateSettingsSchema>;

interface TemplateSettingsCardProps {
  defaultTemplateId: string | null;
  onSave: (data: UpdateDefaultTemplateRequest) => Promise<void>;
  disabled?: boolean;
  isSaving?: boolean;
}

export function TemplateSettingsCard({
  defaultTemplateId,
  onSave,
  disabled = false,
  isSaving = false,
}: TemplateSettingsCardProps) {
  const { t } = useTranslation(["calendar", "common"]);
  const { templates, fetchTemplates } = useTemplatesStore();
  const [isLoading, setIsLoading] = useState(false);
  const [, setTemplateChanged] = useState(false);

  // Initialize form with react-hook-form and zod resolver
  const form = useForm<TemplateSettingsFormValues>({
    resolver: zodResolver(templateSettingsSchema),
    defaultValues: {
      defaultTemplateId: defaultTemplateId,
    },
  });

  // Track if the form has changes
  const isDirty = form.formState.isDirty;

  // Load templates on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoading(true);
      try {
        await fetchTemplates();
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [fetchTemplates]);

  // Update form values when props change
  useEffect(() => {
    form.reset({
      defaultTemplateId: defaultTemplateId,
    });
    setTemplateChanged(false);
  }, [defaultTemplateId, form]);

  // Watch for template changes to show/hide autoFill option
  const watchedTemplateId = form.watch("defaultTemplateId");

  // Update templateChanged state when template changes
  useEffect(() => {
    if (watchedTemplateId !== defaultTemplateId) {
      setTemplateChanged(true);
    } else {
      setTemplateChanged(false);
    }
  }, [watchedTemplateId, defaultTemplateId]);

  // Handle form submission
  const onSubmit = async (data: TemplateSettingsFormValues) => {
    await onSave({
      default_template_id: data.defaultTemplateId,
      auto_fill_new_week: data.defaultTemplateId !== null,
    });
  };

  const action = isDirty ? (
    <Button
      className="gap-1.5"
      disabled={disabled || isSaving}
      size="sm"
      variant="foreground"
      onClick={form.handleSubmit(onSubmit)}
    >
      {isSaving ? (
        <Icon className="h-4 w-4 animate-spin" icon="mdi:loading" />
      ) : (
        <Icon className="h-4 w-4" icon="mdi:content-save" />
      )}
      {t("common:save")}
    </Button>
  ) : null;

  return (
    <SettingsCard
      action={action}
      description={t("calendar:settings.defaultTemplate.description")}
      icon="mdi:calendar-clock"
      title={t("calendar:settings.defaultTemplate.title")}
    >
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="defaultTemplateId"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="text-muted-foreground text-xs">
                  {t("common:selectTemplate")}
                </FormLabel>
                <FormControl>
                  <Select
                    disabled={disabled || isLoading}
                    value={field.value || "none"}
                    onValueChange={(value) => field.onChange(value === "none" ? null : value)}
                  >
                    <SelectTrigger className="bg-muted text-accent-foreground hover:bg-side-accent h-11 border-0 text-sm transition-colors">
                      <SelectValue
                        placeholder={t("calendar:settings.defaultTemplate.selectPlaceholder")}
                      />
                    </SelectTrigger>
                    <SelectContent align={"center"} className={"relative z-[1000]!"} side={"top"}>
                      <SelectItem value="none">
                        {t("calendar:settings.defaultTemplate.none")}
                      </SelectItem>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />
        </form>
      </Form>
    </SettingsCard>
  );
}
